import NumberInput from "../../../components/NumberInput";
import Select from "react-select";
import { FileUploader } from "react-drag-drop-files";
import { toast } from "react-hot-toast";
import { toUnitOptions } from "../../../utils/helpers/unitOptions";
import { toCategoryOptions } from "../../../utils/helpers/categoryOptions";
import { toTypeOptions } from "../../../utils/helpers/typeOptions";
import { getBaseUnitLabel, getPriceUnitLabel } from "../../../utils/helpers/pricing";
import { readImageFile } from "../../../utils/helpers/imageUpload";
import { resolveAssetUrl } from "../../../utils/helpers/storeSetting";
import { useEffect, useMemo, useRef } from "react";
import { FieldFeedback, FormValidationNotice, RequiredMark } from "../../../components/FormValidation";
import useFormValidation from "../../../hooks/useFormValidation";
import { validationRules as rule } from "../../../utils/helpers/formValidation";
import MaterialUnitFields from "./MaterialUnitFields";

const FormMaterial = (props) => {
  const {
    isShowModal,
    isEdit,
    closeModal,
    formState,
    setFormState,
    handleAdd,
    handleUpdate,
    units,
    materialCategories,
    materialTypes,
    isLoading,
  } = props;
  const formRef = useRef(null);
  const validationSchema = useMemo(() => ({
    name: rule.required("Nama material wajib diisi."),
    unit: rule.required("Pilih satuan stok dan resep terlebih dahulu."),
    weight: [rule.number(), rule.greaterThan(0, "Isi per kemasan harus lebih dari 0 atau dikosongkan.")],
    unit_cost: [rule.number(), rule.min(0, "Harga acuan tidak boleh negatif.")],
    ...(!isEdit ? { stock: [rule.number(), rule.min(0, "Stok awal tidak boleh negatif.")] } : {}),
    min_stock: [rule.number(), rule.min(0, "Stok minimum tidak boleh negatif.")],
  }), [isEdit]);
  const validation = useFormValidation({ values: formState, schema: validationSchema, formId: "material", focusRoot: formRef });
  const { resetValidation } = validation;
  useEffect(() => {
    resetValidation();
  }, [isShowModal, formState?.id, resetValidation]);

  const submit = async (handler) => {
    if (!validation.validateAll() || isLoading) return;
    await handler();
  };

  const optionUnit = toUnitOptions(units);
  const optionCategory = toCategoryOptions(materialCategories);
  const optionType = toTypeOptions(materialTypes, [], formState?.category);
  const stockUnitLabel = getBaseUnitLabel(optionUnit, formState?.unit);
  const priceUnitLabel = getPriceUnitLabel(optionUnit, formState?.unit);
  return (
    <div
      className="modal modal-blur fade "
      id="modal-form"
      tabIndex="-1"
      role="dialog"
      aria-hidden="false"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.3)",
      }}>
      <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
        <div ref={formRef} className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Master Material {!isEdit ? "Baru" : "Edit"}</h5>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Tutup"
              onClick={closeModal}></button>
          </div>
          <div className="modal-body">
            <FormValidationNotice errors={validation.errors} />
            <div className="row">
              <div className="col-lg-12">
                <div className="row">
                  <div className="col-lg-12">
                    <label className="form-label">Nama Material<RequiredMark /></label>
                    <div className="mb-3 input-icon">
                      <span className="input-icon-addon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="icon icon-1">
                          <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0"></path>
                          <path d="M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2"></path>
                        </svg>
                      </span>
                      <input
                        {...validation.fieldProps("name")}
                        type="text"
                        className={validation.inputClass("name")}
                        placeholder="material"
                        value={formState?.name || ""}
                        onChange={(e) =>
                          setFormState((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                      />
                      <FieldFeedback formId="material" name="name" error={validation.errors.name} hint="Gunakan nama yang mudah dicari saat pembelian dan membuat resep." />
                    </div>
                  </div>
                  <div className="col-lg-12">
                    <MaterialUnitFields
                      key={`${formState?.id || "new"}-${Boolean(isShowModal)}`}
                      formState={formState}
                      setFormState={setFormState}
                      options={optionUnit}
                      stockUnitLabel={stockUnitLabel}
                      validation={validation}
                      isLoading={isLoading}
                    />
                  </div>
                </div>
              </div>
              <div className="col-lg-12">
                <div className="row">
                  <div className="col-lg-6">
                    <label className="form-label">Kategori</label>
                    <div className="mb-3 input-icon">
                      <span
                        className="input-icon-addon"
                        style={{
                          zIndex: 1,
                        }}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="icon icon-tabler icons-tabler-outline icon-tabler-paper-bag">
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M8 3h8a2 2 0 0 1 2 2v1.82a5 5 0 0 0 .528 2.236l.944 1.888a5 5 0 0 1 .528 2.236v5.82a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-5.82a5 5 0 0 1 .528 -2.236l1.472 -2.944v-3a2 2 0 0 1 2 -2" />
                          <path d="M12 15a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
                          <path d="M6 21a2 2 0 0 0 2 -2v-5.82a5 5 0 0 0 -.528 -2.236l-1.472 -2.944" />
                          <path d="M11 7h2" />
                        </svg>
                      </span>
                      <Select
                        options={optionCategory || []}
                        name="category"
                        className="form-select p-0"
                        classNamePrefix="akas-select"
                        styles={{
                          control: (base) => ({
                            ...base,
                            paddingLeft: "2rem", // beri ruang untuk ikon
                            border: "none",
                            borderRadius: "8px",
                            boxShadow: "none",
                            minHeight: "38px",
                            "&:hover": {
                              border: "none",
                              boxShadow: "none",
                            },
                          }),
                          multiValue: (base) => ({
                            ...base,
                            backgroundColor: "#e3f2fd", // soft light blue
                            borderRadius: "20px",
                            padding: "2px 8px",
                          }),
                          multiValueLabel: (base) => ({
                            ...base,
                            color: "#a7f3d0", // soft blue text
                            fontWeight: "500",
                          }),
                          multiValueRemove: (base) => ({
                            ...base,
                            color: "#a7f3d0",
                            ":hover": {
                              backgroundColor: "#bbdefb", // hover soft blue
                              color: "#a7f3d0",
                            },
                          }),
                          // list dropdown style
                          menu: (base) => ({
                            ...base,
                            zIndex: 2,
                          }),
                          placeholder: (base) => ({
                            ...base,
                            color: "#90a4ae", // muted blue-gray
                          }),
                          option: (base, state) => ({
                            ...base,
                            backgroundColor: state.isSelected
                              ? "#32CD32" // item yang dipilih
                              : state.isFocused
                                ? "#90EE90" // saat hover
                                : "#FFF", // default
                            color: state.isSelected
                              ? "#FFF" // item yang dipilih
                              : state.isFocused
                                ? "#000" // saat hover
                                : "#000", // default
                            cursor: "pointer",
                          }),
                        }}
                        value={
                          formState?.category
                            ? optionCategory.find((option) => option.value === formState?.category)
                            : null
                        }
                        onChange={(selectedOptions) => {
                          setFormState({
                            ...formState,
                            category: selectedOptions?.value || "",
                            type: "",
                          });
                        }}
                        isDisabled={optionCategory.length === 0}
                        noOptionsMessage={() => "Belum ada kategori material"}
                        placeholder={
                          optionCategory.length ? "-- pilih kategori --" : "Tambah kategori di Master Kategori"
                        }
                      />
                      {optionCategory.length === 0 ? (
                        <div className="form-hint text-warning">Kategori material belum dibuat untuk toko ini.</div>
                      ) : null}
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <label className="form-label">Tipe</label>
                    <div className="mb-3 input-icon">
                      <span
                        className="input-icon-addon"
                        style={{
                          zIndex: 1,
                        }}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="icon icon-tabler icons-tabler-outline icon-tabler-coffee">
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M3 14c.83 .642 2.077 1.017 3.5 1c1.423 .017 2.67 -.358 3.5 -1c.83 -.642 2.077 -1.017 3.5 -1c1.423 -.017 2.67 .358 3.5 1" />
                          <path d="M8 3a2.4 2.4 0 0 0 -1 2a2.4 2.4 0 0 0 1 2" />
                          <path d="M12 3a2.4 2.4 0 0 0 -1 2a2.4 2.4 0 0 0 1 2" />
                          <path d="M3 10h14v5a6 6 0 0 1 -6 6h-2a6 6 0 0 1 -6 -6v-5" />
                          <path d="M16.746 16.726a3 3 0 1 0 .252 -5.555" />
                        </svg>
                      </span>
                      <Select
                        options={optionType || []}
                        name="type"
                        className="form-select p-0"
                        classNamePrefix="akas-select"
                        styles={{
                          control: (base) => ({
                            ...base,
                            paddingLeft: "2rem", // beri ruang untuk ikon
                            border: "none",
                            borderRadius: "8px",
                            boxShadow: "none",
                            minHeight: "38px",
                            "&:hover": {
                              border: "none",
                              boxShadow: "none",
                            },
                          }),
                          multiValue: (base) => ({
                            ...base,
                            backgroundColor: "#e3f2fd", // soft light blue
                            borderRadius: "20px",
                            padding: "2px 8px",
                          }),
                          multiValueLabel: (base) => ({
                            ...base,
                            color: "#a7f3d0", // soft blue text
                            fontWeight: "500",
                          }),
                          multiValueRemove: (base) => ({
                            ...base,
                            color: "#a7f3d0",
                            ":hover": {
                              backgroundColor: "#bbdefb", // hover soft blue
                              color: "#a7f3d0",
                            },
                          }),
                          // list dropdown style
                          menu: (base) => ({
                            ...base,
                            zIndex: 2,
                          }),
                          placeholder: (base) => ({
                            ...base,
                            color: "#90a4ae", // muted blue-gray
                          }),
                          option: (base, state) => ({
                            ...base,
                            backgroundColor: state.isSelected
                              ? "#32CD32" // item yang dipilih
                              : state.isFocused
                                ? "#90EE90" // saat hover
                                : "#FFF", // default
                            color: state.isSelected
                              ? "#FFF" // item yang dipilih
                              : state.isFocused
                                ? "#000" // saat hover
                                : "#000", // default
                            cursor: "pointer",
                          }),
                        }}
                        value={formState?.type ? optionType.find((option) => option.value === formState?.type) : null}
                        onChange={(selectedOptions) => {
                          setFormState({
                            ...formState,
                            type: selectedOptions?.value || "",
                          });
                        }}
                        isDisabled={!formState?.category || optionType.length === 0}
                        noOptionsMessage={() => "Belum ada tipe untuk kategori ini"}
                        placeholder={
                          !formState?.category
                            ? "-- pilih kategori dulu --"
                            : optionType.length
                              ? "-- pilih tipe --"
                              : "Tambah tipe di Master Tipe"
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-12">
                <div className="row">
                  <div className="col-lg-12">
                    <label className="form-label">Harga Acuan {formState?.unit ? `(per ${priceUnitLabel})` : ""}<RequiredMark optional /></label>
                    <div className="mb-3 input-icon">
                      <span className="input-icon-addon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="icon icon-tabler icons-tabler-outline icon-tabler-cash-banknote">
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0" />
                          <path d="M3 8a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v8a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2l0 -8" />
                          <path d="M18 12h.01" />
                          <path d="M6 12h.01" />
                        </svg>
                      </span>
                      <NumberInput
                        {...validation.fieldProps("unit_cost")}
                        type="number"
                        onWheel={(e) => e.target.blur()}
                        min="0"
                        className={validation.inputClass("unit_cost")}
                        placeholder="harga"
                        value={formState?.unit_cost || ""}
                        onChange={(e) =>
                          setFormState((prev) => ({
                            ...prev,
                            unit_cost: e.target.value,
                          }))
                        }
                      />
                      <FieldFeedback formId="material" name="unit_cost" error={validation.errors.unit_cost} hint="Tidak boleh negatif; biaya aktual akan diperbarui dari transaksi pembelian." />
                    </div>
                  </div>
                  {/* <div className="col-lg-3">
                    <label className="form-label">
                      Isi per Satuan Pembelian {formState?.unit ? `(${stockUnitLabel})` : ""}
                    </label>
                    <div className="mb-3 input-icon">
                      <span className="input-icon-addon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="icon icon-tabler icons-tabler-outline icon-tabler-scale-outline">
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M3 7a4 4 0 0 1 4 -4h10a4 4 0 0 1 4 4v10a4 4 0 0 1 -4 4h-10a4 4 0 0 1 -4 -4v-10" />
                          <path d="M12 7c1.956 0 3.724 .802 5 2.095l-2.956 2.904a3 3 0 0 0 -2.038 -.799a3 3 0 0 0 -2.038 .798l-2.956 -2.903a6.979 6.979 0 0 1 5 -2.095" />
                        </svg>
                      </span>
                      <input
                        type="number"
                        onWheel={(e) => e.target.blur()}
                        className="form-control"
                        name="weight"
                        placeholder={`contoh: 1 ${formState?.unit || "kemasan"} berisi berapa ${stockUnitLabel}`}
                        value={formState?.weight || ""}
                        onChange={(e) =>
                          setFormState((prev) => ({
                            ...prev,
                            weight: e.target.value,
                          }))
                        }
                      />
                      <div className="form-hint">
                        Isi dalam satuan dasar. Contoh: 1 kg = 1.000 gram, 1 liter = 1.000 ml.
                      </div>
                    </div>
                  </div> */}
                </div>
              </div>
              <div className="col-lg-12">
                <div className="row">
                  {!isEdit && (
                    <div className="col-lg-6">
                      <label className="form-label">Stok Awal ({stockUnitLabel})<RequiredMark optional /></label>
                      <div className="mb-3 input-icon">
                        <span className="input-icon-addon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="icon icon-tabler icons-tabler-outline icon-tabler-tip-jar">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M14 10h-2.5a1.5 1.5 0 0 0 0 3h1a1.5 1.5 0 0 1 0 3h-2.5" />
                            <path d="M12 9v1" />
                            <path d="M12 16v1" />
                            <path d="M17 4v1.882c0 .685 .387 1.312 1 1.618s1 .933 1 1.618v8.882a3 3 0 0 1 -3 3h-8a3 3 0 0 1 -3 -3v-8.882c0 -.685 .387 -1.312 1 -1.618s1 -.933 1 -1.618v-1.882" />
                            <path d="M6 4h12l-12 0" />
                          </svg>
                        </span>
                        <NumberInput
                          {...validation.fieldProps("stock")}
                          type="number"
                          onWheel={(e) => e.target.blur()}
                          min="0"
                          className={validation.inputClass("stock")}
                          placeholder={`stok dalam ${stockUnitLabel}`}
                          value={formState?.stock || ""}
                          onChange={(e) =>
                            setFormState((prev) => ({
                              ...prev,
                              stock: e.target.value,
                            }))
                          }
                        />
                        <FieldFeedback formId="material" name="stock" error={validation.errors.stock} hint="Kosong berarti stok awal 0." />
                      </div>
                    </div>
                  )}
                  <div className={isEdit ? "col-lg-12" : "col-lg-6"}>
                    <label className="form-label">Stok Minimum ({stockUnitLabel})<RequiredMark optional /></label>
                    <div className="mb-3 input-icon">
                      <span className="input-icon-addon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="icon icon-tabler icons-tabler-outline icon-tabler-info-square-rounded">
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M12 9h.01" />
                          <path d="M11 12h1v4h1" />
                          <path d="M12 3c7.2 0 9 1.8 9 9c0 7.2 -1.8 9 -9 9c-7.2 0 -9 -1.8 -9 -9c0 -7.2 1.8 -9 9 -9" />
                        </svg>
                      </span>
                      <NumberInput
                        {...validation.fieldProps("min_stock")}
                        type="number"
                        onWheel={(e) => e.target.blur()}
                        min="0"
                        className={validation.inputClass("min_stock")}
                        placeholder={`stok minimum dalam ${stockUnitLabel}`}
                        value={formState?.min_stock || ""}
                        onChange={(e) =>
                          setFormState((prev) => ({
                            ...prev,
                            min_stock: e.target.value,
                          }))
                        }
                      />
                      <FieldFeedback formId="material" name="min_stock" error={validation.errors.min_stock} hint="Dipakai untuk peringatan stok menipis; kosong berarti 0." />
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-12">
                <label className="form-label">Deskripsi</label>
                <div className="mb-3 input-icon">
                  <span className="input-icon-addon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="icon icon-1">
                      <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0"></path>
                      <path d="M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2"></path>
                    </svg>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    name="description"
                    placeholder="deskripsi"
                    value={formState?.description || ""}
                    onChange={(e) =>
                      setFormState((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>
              <div className="col-lg-12">
                <label className="form-label">Foto Material</label>
                <div className="mb-3 w-100 ">
                  <div className="card  d-flex justify-content-center align-content-center w-100">
                    <DragDropFile setFormState={setFormState} formState={formState} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-link link-secondary btn-3"
              data-bs-dismiss="modal"
              onClick={closeModal}>
              Batal
            </button>
            {isEdit ? (
              <button
                type="button"
                className="btn btn-primary btn-5 ms-auto"
                disabled={isLoading}
                onClick={() => submit(handleUpdate)}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="icon icon-tabler icons-tabler-outline icon-tabler-edit">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1" />
                  <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z" />
                  <path d="M16 5l3 3" />
                </svg>
                {isLoading ? "Menyimpan..." : "Simpan Perubahan"}
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-primary btn-5 ms-auto"
                disabled={isLoading}
                onClick={() => submit(handleAdd)}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="icon icon-2">
                  <path d="M12 5l0 14" />
                  <path d="M5 12l14 0" />
                </svg>
                {isLoading ? "Menyimpan..." : "Tambah Material"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
const DragDropFile = (props) => {
  const { setFormState, formState } = props;
  const fileTypes = ["JPG", "JPEG", "PNG", "WEBP"];

  return (
    <FileUploader
      multiple={false}
      handleChange={async (file) => {
        try {
          const base64 = await readImageFile(file, { label: "Foto material", preset: "catalog" });
          setFormState((previous) => ({
            ...previous,
            image: base64,
            image_path: base64,
            imageUrl: base64,
            file_name: file?.name,
          }));
        } catch (error) {
          toast.error(error?.message || "Foto material tidak dapat dibaca.");
        }
      }}
      name="file"
      types={fileTypes}
      dropMessageStyle={{ margin: "0  0.5rem" }}
      style={{ position: "relative" }}>
      <div className="d-flex align-items-center justify-content-center w-100 ">
        <label
          htmlFor="dropzone-file"
          className="d-flex flex-column align-items-center justify-content-center w-100  rounded cursor-pointer bg-light"
          style={{ height: "16rem" }}>
          <div className="d-flex flex-column align-items-center justify-content-center pt-3 pb-3">
            <svg
              className="mb-3 text-secondary"
              width="32"
              height="32"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 20 16">
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
              />
            </svg>
            <p className="mb-2 text-muted small">
              <strong>Click Untuk upload</strong> atau drag and drop
            </p>
            <p className="text-muted small">JPG, PNG, atau WEBP (maks. 6 MB, dioptimalkan otomatis)</p>
          </div>
        </label>
      </div>

      {formState?.image_path || formState?.image || formState?.imageUrl ? (
        <div
          className="d-flex align-items-center justify-content-center w-100 position-absolute bottom-0 z-0 border border-primary rounded transition-opacity"
          style={{
            height: "16rem",
            opacity: "1",
          }}
          onMouseOver={(e) => (e.currentTarget.style.opacity = "0.5")}
          onMouseOut={(e) => (e.currentTarget.style.opacity = "1")}>
          <img
            src={resolveAssetUrl(formState?.imageUrl || formState?.image_path || formState?.image, "")}
            className="rounded h-100 w-100 p-2 object-fit-contain"
            alt="Uploaded preview"
          />
        </div>
      ) : (
        ""
      )}
    </FileUploader>
  );
};

export default FormMaterial;
